/* Карточка товара и её части: звёзды, отзыв, скелетон. */
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { imageUrl } from '../lib/api.js';
import { money, formatDate } from '../lib/format.js';
import { useTilt } from '../hooks/useMotion.js';

/* ────────────────────────────  Звёзды  ─────────────────────────── */

export function Stars({ rating = 5, size = 14 }) {
  const full = Math.round(Number(rating) || 0);
  return (
    <span className="stars" style={{ fontSize: size }} aria-label={`Оценка ${full} из 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <i key={i} className={i <= full ? '' : 'is-off'} style={{ animationDelay: `${i * 55}ms` }}>
          ★
        </i>
      ))}
    </span>
  );
}

/* ─────────────────  Слой изображения или плейсхолдер  ───────────── */

export function Media({ image, alt, className = '', active = true, eager = false }) {
  if (image?.src) {
    return (
      <img
        className={`${className} product-card__img${active ? ' is-on' : ''}`}
        src={imageUrl(image.src)}
        alt={image.alt || alt}
        style={{ objectPosition: image.pos || '50% 50%' }}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
      />
    );
  }
  return (
    <div className={`${className} product-card__ph hatch${active ? ' is-on' : ''}`}>
      <span>{image?.label || 'фото'}</span>
    </div>
  );
}

/* ──────────────────────────  Карточка товара  ──────────────────── */

export function ProductCard({ product, index = 0, onAdd }) {
  const navigate = useNavigate();
  const cardRef = useTilt({ max: 4 });
  const mediaRef = useRef(null);
  const [frame, setFrame] = useState(0);

  const images = product.images?.length ? product.images : [{ src: null, label: 'фото', alt: product.name }];
  const open = () => navigate(`/product/${product.slug}`);

  // Курсор делит фото на равные зоны — снимки листаются без открытия карточки
  const onMove = (e) => {
    if (images.length < 2) return;
    const r = mediaRef.current.getBoundingClientRect();
    const idx = Math.max(0, Math.min(images.length - 1,
      Math.floor(((e.clientX - r.left) / r.width) * images.length)));
    if (idx !== frame) setFrame(idx);
  };

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const source = mediaRef.current?.querySelector('.is-on');
    onAdd?.(product, source);
  };

  return (
    <article className="product-card" data-reveal="up" data-flip-key={product.slug} ref={cardRef}>
      <div
        className="product-card__media"
        ref={mediaRef}
        role="button"
        tabIndex={0}
        aria-label={`Открыть карточку: ${product.name}`}
        onMouseMove={onMove}
        onMouseLeave={() => setFrame(0)}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        }}
      >
        {images.map((img, i) => (
          <Media key={i} image={img} alt={product.name} active={i === frame} eager={index < 3} />
        ))}

        {product.tag ? <span className="product-card__tag">{product.tag}</span> : null}

        {images.length > 1 ? (
          <div className="product-card__dots">
            {images.map((_, i) => (
              <i key={i} className={i === frame ? 'is-on' : ''} />
            ))}
          </div>
        ) : null}

        <div className="product-card__hint">
          {images.length > 1 ? 'Наведите — фото листаются' : 'Открыть карточку'}
        </div>
      </div>

      <div className="product-card__body">
        <span className="product-card__cat">{product.category.title}</span>
        <Link className="product-card__name" to={`/product/${product.slug}`}>
          {product.name}
        </Link>
        <div className="product-card__foot">
          <span className="product-card__price">{money(product.price)}</span>
          {product.inStock ? (
            <button className="btn btn--dark btn--sm" type="button" onClick={handleAdd}>
              В корзину
            </button>
          ) : (
            <span className="product-card__out">Нет в наличии</span>
          )}
        </div>
      </div>
    </article>
  );
}

/* ────────────────────────────  Отзыв  ─────────────────────────── */

export function ReviewCard({ review, dark = false, showProduct = false }) {
  const meta = [
    review.size ? `Размер ${review.size}` : null,
    showProduct ? review.productName : null,
    formatDate(review.createdAt),
  ].filter(Boolean).join(' · ');

  return (
    <article className={`review${dark ? ' review--dark' : ''}`} data-reveal="up">
      <Stars rating={review.rating} />
      <p className="review__text">{review.text}</p>
      <div className="review__who">
        <span className="review__author-row">
          <span className="review__author">{review.author}</span>
          {review.verified ? <VerifiedBadge /> : null}
        </span>
        <span className="review__meta">{meta}</span>
      </div>
    </article>
  );
}

/** Отзыв от того, кто действительно покупал этот товар. */
export function VerifiedBadge() {
  return (
    <span className="verified" title="Автор покупал этот товар в магазине">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Проверенная покупка
    </span>
  );
}

/* ──────────────────────────  Скелетон  ────────────────────────── */

export function ProductSkeleton() {
  return (
    <div className="product-card">
      <div className="skeleton" style={{ height: 330, borderRadius: 0 }} />
      <div className="product-card__body">
        <div className="skeleton" style={{ height: 12, width: '38%' }} />
        <div className="skeleton" style={{ height: 16, width: '72%', marginTop: 8 }} />
        <div className="skeleton" style={{ height: 34, width: '100%', marginTop: 16 }} />
      </div>
    </div>
  );
}
